// src/components/RemedySection.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import PlanetRemediesPanel from "./PlanetRemediesPanel";

const PLANET_HINDI = {
  Sun: "सूर्य",
  Moon: "चन्द्र",
  Mars: "मंगल",
  Mercury: "बुध",
  Jupiter: "गुरु",
  Venus: "शुक्र",
  Saturn: "शनि",
  Rahu: "राहु",
  Ketu: "केतु",
};

function formatDateRange(seg) {
  if (!seg) return "-";
  return `${seg.start} → ${seg.end}`;
}

export default function RemedySection({ remedies, dasha, mode = "REAL", plan = "STARTER" }) {
  // =====================================================
  // ✅ HOOKS FIRST — ALWAYS
  // =====================================================
  const [selectedPlanetFromHeaderCard, setSelectedPlanetFromHeaderCard] =
    useState(null);

  const remediesRef = useRef(null);

  const isMobile = typeof window !== "undefined" && window.innerWidth < 640;

  const md = dasha?.md;
  const ad = dasha?.ad;
  const pd = dasha?.pd;

  const activeRoles = useMemo(() => {
    const roles = {};
    if (md?.planet) roles[md.planet] = "MD";
    if (ad?.planet) roles[ad.planet] = "AD";
    if (pd?.planet) roles[pd.planet] = "PD";
    return roles;
  }, [md?.planet, ad?.planet, pd?.planet]);

  // =====================================================
  // ✅ Scroll to planet remedies section when header-card clicked
  // =====================================================
  useEffect(() => {
    if (!selectedPlanetFromHeaderCard) return;

    const t = setTimeout(() => {
      if (!remediesRef.current) return;
      remediesRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 60);

    return () => clearTimeout(t);
  }, [selectedPlanetFromHeaderCard]);

  // =====================================================
  // ✅ Defensive return AFTER hooks
  // =====================================================
  if (!remedies) return null;

  const baseCard = {
    borderRadius: 12,
    border: "1px solid rgba(148,163,184,0.18)",
    background: "rgba(15,23,42,0.92)",
    boxShadow: "0 10px 28px rgba(0,0,0,0.28)",
  };

  const dashaCardBase = {
    borderRadius: 12,
    padding: 12,
    border: "1px solid rgba(148,163,184,0.18)",
    cursor: "pointer",
    transition: "box-shadow 120ms ease, border 120ms ease",
  };

  const onClickHeaderCard = (planet) => {
    if (!planet) return;
    setSelectedPlanetFromHeaderCard(planet);
  };

  // ✅ DATA-DRIVEN GATING (NEW)

  const hasRealDasha =
    !!md?.planet && !!ad?.planet && !!pd?.planet;

  // Starter case = backend gives no real dasha planets
  const dashaCardsLocked = !hasRealDasha;

  // LIMITED remedies detection
  const isLimitedRemedies =
    Object.keys(remedies?.by_planet || {}).length <= 1;

  return (
    <div style={{ fontSize: 13 }}>
      {/* =====================================================
          0) TOP HEADER
      ====================================================== */}
      <div style={{ marginBottom: 14, marginTop: 10 }}>
        <div
          style={{
            marginBottom: 6,
          }}
        >
          <div
            style={{
              fontSize: 20,
              fontWeight: 800,
              color: "#fbbf24",
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            🪔 Karmic Remedies
          </div>
        </div>

        <div style={{ color: "#9ca3af", fontSize: 13, lineHeight: 1.55 }}>
          Remedies are daily balancing actions — not superstition or guarantees.
          Use them to stabilize patterns, not to force outcomes.
        </div>

      </div>

      {/* =====================================================
          1) CURRENT DASHA PERIOD CARD
      ====================================================== */}
      <div
        style={{
          ...baseCard,
          marginBottom: 16,
          padding: isMobile ? 12 : 14,
          border: "1px solid rgba(245,158,11,0.28)",
          background:
            "radial-gradient(circle at top left, rgba(245,158,11,0.08), rgba(15,23,42,0.92))",
        }}
      >
        {/* Header row */}
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontWeight: 600, fontSize: 15, color: "#f8fafc" }}>
            🕒 Your Active Cycle
          </div>
        </div>

        {/* One-line summary */}
        <div
          style={{
            fontSize: 12,
            color: "#e5e7eb",
            marginBottom: 12,
            lineHeight: 1.6,
          }}
        >
          {dashaCardsLocked
            ? "Your active planetary cycle is available in Growth and Insight plans. The practices below reflect your chart’s essential balancing actions."
            : "Click any Dasha card below to jump to that planet’s practices."}
        </div>

        {/* MD / AD / PD cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr",
            gap: 12,
            marginBottom: 14,
          }}
        >
          {/* MD */}
          <div
            onClick={() => {
              if (dashaCardsLocked) return;
              if (md?.planet) onClickHeaderCard(md.planet);
            }}
            style={{
              ...dashaCardBase,
              opacity: dashaCardsLocked ? 0.55 : 1,
              cursor: dashaCardsLocked ? "not-allowed" : "pointer",
              background:
                "linear-gradient(135deg, rgba(245,158,11,0.30), rgba(15,23,42,0.85))",
            }}
          >
            <div style={{ fontSize: 11, color: "rgba(245,158,11,0.75)" }}>Mahadasha (Life Chapter)</div>
            <div
              style={{
                fontSize: 14,
                fontWeight: 800,
                color: "#f8fafc",
                marginTop: 2,
                marginBottom: 6,
              }}
            >
              {dashaCardsLocked
                ? "Available in Growth"
                : md?.planet
                ? `${md.planet} (${PLANET_HINDI[md.planet] || ""})`
                : "—"}
            </div>
            <div style={{ fontSize: 12, color: "#e5e7eb" }}>
              {dashaCardsLocked
                ? "Unlock to view"
                : formatDateRange(md)}
            </div>
          </div>

          {/* AD */}
          <div
            onClick={() => {
              if (dashaCardsLocked) return;
              if (ad?.planet) onClickHeaderCard(ad.planet);
            }}
            style={{
              ...dashaCardBase,
              opacity: dashaCardsLocked ? 0.55 : 1,
              cursor: dashaCardsLocked ? "not-allowed" : "pointer",
              background:
                "linear-gradient(135deg, rgba(56,189,248,0.30), rgba(15,23,42,0.85))",
            }}
          >
            <div style={{ fontSize: 11, color: "rgba(34,211,238,0.75)" }}>Antardasha (Current theme)</div>
            <div
              style={{
                fontSize: 14,
                fontWeight: 800,
                color: "#f8fafc",
                marginTop: 2,
                marginBottom: 6,
              }}
            >
              {dashaCardsLocked
                ? "Available in Growth"
                : ad?.planet
                ? `${ad.planet} (${PLANET_HINDI[ad.planet] || ""})`
                : "—"}
            </div>
            <div style={{ fontSize: 12, color: "#e5e7eb" }}>
              {dashaCardsLocked ? "Unlock to view" : formatDateRange(ad)}
            </div>
          </div>

          {/* PD */}
          <div
            onClick={() => {
              if (dashaCardsLocked) return;
              if (pd?.planet) onClickHeaderCard(pd.planet);
            }}
            style={{
              ...dashaCardBase,
              opacity: dashaCardsLocked ? 0.55 : 1,
              cursor: dashaCardsLocked ? "not-allowed" : "pointer",
              background:
                "linear-gradient(135deg, rgba(129,140,248,0.30), rgba(15,23,42,0.85))",
            }}
          >
            <div style={{ fontSize: 11, color: "rgba(168,85,247,0.75)" }}>Pratyantar (Day-to-day Intensity)</div>
            <div
              style={{
                fontSize: 14,
                fontWeight: 800,
                color: "#f8fafc",
                marginTop: 2,
                marginBottom: 6,
              }}
            >
              {dashaCardsLocked
                ? "Available in Growth"
                : pd?.planet
                ? `${pd.planet} (${PLANET_HINDI[pd.planet] || ""})`
                : "—"}
            </div>
            <div style={{ fontSize: 12, color: "#e5e7eb" }}>
              {dashaCardsLocked ? "Unlock to view" : formatDateRange(pd)}
            </div>
          </div>
        </div>

        {/* Divider */}
        <div
          style={{
            margin: "2px 0 14px",
            borderTop: "1px dashed rgba(148,163,184,0.18)",
          }}
        />

        {/* Practical order */}
        <div
          style={{
            borderRadius: 12,
            padding: 12,
            border: "1px solid rgba(148,163,184,0.16)",
            background: "rgba(2,6,23,0.35)",
            borderLeft: "4px solid rgba(245,158,11,0.75)",
          }}
        >
          <div
            style={{
              fontSize: 13,
              color: "#cbd5e1",
              lineHeight: 1.7
            }}
          >
          <b style={{ color: "rgba(251,191,36,0.90)", fontWeight: 600 }}>Practical flow of guidance</b>

          <ol style={{marginTop:8, paddingLeft:18}}>

          <li>
          Start with the <b>Mahadasha</b> — it names the long-term chapter
          shaping your life right now.
          </li>

          <li>
          The <b>Antardasha</b> narrows focus to the specific situations
          and themes active within that chapter.
          </li>

          <li>
          The <b>Pratyantardasha</b> reflects short-term intensity —
          where small, consistent practices have the most effect.
          </li>

          </ol>

          <div style={{marginTop:8, fontSize:12, color:"#94a3b8"}}>
          Begin with Primary practices and let them settle before
          layering in Secondary or Tertiary.
          </div>

</div>
        </div>
      </div>
      
      {/* =====================================================
          2) DASHA-WISE REMEDIES
      ====================================================== */}
      <div
        ref={remediesRef}
        style={{
          ...baseCard,
          marginBottom: 12,
          padding: isMobile ? 12 : 14,
        }}
      >
        <div style={{ marginBottom: 10 }}>
         <div
            style={{
              fontWeight: 600,
              fontSize: 15,
              color: "#f8fafc",
              marginBottom: 4,
            }}
          >
            🌿 Practices for This Cycle
          </div>

          <div style={{ fontSize: 12, color: "#9ca3af", lineHeight: 1.55 }}>
            These remedies are generated only for your current MD / AD / PD period.
          </div>


          {/* Tiny improvement: show locked hint when only 1 planet exists */}
          {isLimitedRemedies && (
            <div style={{ marginTop: 10, fontSize: 12, color: "#9ca3af" }}>
              🔒 Additional planetary remedies unlock in{" "}
              <b style={{ color: "#fbbf24" }}>Growth</b>.
            </div>
          )}
        </div>

        <PlanetRemediesPanel
          byPlanet={remedies.by_planet || {}}
          activeRoles={activeRoles}
          mdPlanet={md?.planet || null}
          adPlanet={ad?.planet || null}
          pdPlanet={pd?.planet || null}
          selectedPlanetExternal={selectedPlanetFromHeaderCard}
          onSelectedPlanetChange={setSelectedPlanetFromHeaderCard}
          mode={mode}
          plan={plan}
        />

      </div>

      {/* =====================================================
          3) FUTURE FEATURE — Full Planet Library (all planets)
      ====================================================== */}
      <details
        style={{
          marginTop: 10,
          borderRadius: 12,
          border: "1px solid rgba(148,163,184,0.14)",
          background: "rgba(15,23,42,0.35)",
          padding: 10,
        }}
      >
        <summary
          style={{
            cursor: "pointer",
            listStyle: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            color: "#9ca3af",
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          <span>🪐 Experimental: Full planet remedies library (future feature)</span>
          <span style={{ color: "#94a3b8", fontSize: 14 }}>▾</span>
        </summary>

        <div
          style={{
            marginTop: 10,
            padding: 12,
            borderRadius: 12,
            background: "rgba(2,6,23,0.35)",
            border: "1px solid rgba(148,163,184,0.14)",
            color: "#e5e7eb",
            lineHeight: 1.6,
            fontSize: 12,
          }}
        >
          <div style={{ marginBottom: 6, fontWeight: 700 }}>
            Coming soon: Remedies for all 9 planets
          </div>

          <div>
            Right now, this tab shows remedies only for your{" "}
            <strong>active Dasha planets</strong> (MD / AD / PD).
            <br />
            In future releases, you’ll be able to explore remedies for{" "}
            <strong>all planets</strong> — even those not active in your current Dasha.
          </div>
        </div>
      </details>

    </div>
  );
}
